import SubtitlesOff from '@mui/icons-material/SubtitlesOff';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { EmptyStateMessage } from '~components/Common/EmptyStateMessage';
import InvoicesTabContent from '~components/Invoice/InvoicesTabContent';
import { Skeleton } from '~components/Skeleton/Skeleton';
import { SmallTabs } from '~components/Tabs/SmallTabs';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import type { DriverDay } from '~hooks/useDriverDays/models';
import { useDriverDays } from '~hooks/useDriverDays/useDriverDays';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import { Job } from '~hooks/useJob/models';
import { ApprovalsQueryContextProvider } from '~pages/Approvals/DriverDay/contexts/ApprovalsQueryContext';
import { useStores } from '~store/RootStore';

import CategoryApprovalsCard from './components/CategoryApprovalsCard';
import JobDetails, { JobDetailsTabs, JobHeader } from './components/JobDetails';
import { ShiftDetails } from './components/ShiftDetails';

interface DriverDayDetailsProps {
  driverDay: DriverDay;
  onDriverDayChange: (driverDay: DriverDay) => void;
}

export const DriverDayDetails = observer(function DriverDayDetails({
  driverDay,
  onDriverDayChange,
}: DriverDayDetailsProps) {
  const driverDayId = driverDay.id;
  const theme = useTheme();
  const [toggledJob, setToggledJob] = useState<Job | null>(null);

  const { driverDayStore, userStore } = useStores();
  const { canApproveDriverDays } = userStore.getPermissions();
  const { fetchJobsForDriverDay, isLoadingDriverDayJobs } = useDriverDays();
  const {
    approveInvoice,
    unapproveInvoice,
    getDriverDayPayables,
    getDriverDayReceivables,
  } = useInvoices();
  const [currentCategory, setCurrentCategory] = useState<InvoiceCategoryFilter>(
    InvoiceCategoryFilter.PAYABLES,
  );
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const getInvoices = async () => {
    const fn =
      currentCategory === InvoiceCategoryFilter.PAYABLES
        ? getDriverDayPayables
        : getDriverDayReceivables;
    const invoices = await fn(driverDayId);
    if (invoices) {
      setInvoices(invoices);
    }
  };

  const jobIdsForDay = driverDayStore.jobIdsByDriverDayId[driverDayId];
  const jobsForDay = useMemo(
    () => jobIdsForDay?.map((jobId) => driverDayStore.jobByJobId[jobId]),
    [jobIdsForDay, driverDayStore.jobByJobId],
  );

  const isLoading =
    isLoadingDriverDayJobs || !jobsForDay || driverDayStore.isLoadingAddOnChargesByJob;

  const { currencyFormatter } = useCompanyCurrency();

  const getInvoicesForJob = useCallback(
    (jobId: string) => {
      return invoices.filter((invoice) => invoice.job?.id === jobId);
    },
    [invoices],
  );
  useEffect(() => {
    getInvoices();
  }, [currentCategory]);

  const DriverInvoices = () => {
    if (!jobsForDay.length) {
      const messageKey =
        currentCategory === InvoiceCategoryFilter.PAYABLES
          ? 'approvals.driver_day.no_payables_available'
          : 'approvals.driver_day.no_receivables_available';
      return <EmptyStateMessage message={t(messageKey)} />;
    }

    return jobsForDay.map((job) => {
      const filteredInvoices = getInvoicesForJob(job.id);
      if (!filteredInvoices.length) {
        return (
          <Box
            key={job.id}
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
            }}
          >
            {' '}
            <Box
              key={job.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mb: 2,
                width: '100%',

                gap: 1.5,

                backgroundColor: theme.semantic.surfaceRaised,
                border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                borderRadius: 2,
                pb: 1,
              }}
            >
              <JobHeader
                job={job}
                hideMapToggle
                toggledJob={toggledJob}
                onToggleJob={setToggledJob}
              />
              <SubtitlesOff style={{ marginInline: 'auto' }} />
              <Typography
                color={theme.brandV2.colors.treadBlack}
                variant="body1"
                sx={{ textAlign: 'center', fontWeight: 600 }}
              >
                {t('approvals.driver_day.no_invoices', {
                  category: currentCategory,
                })}
              </Typography>
            </Box>
          </Box>
        );
      }
      return filteredInvoices.map((invoice) => {
        return (
          <InvoicesTabContent
            invoice={invoice}
            job={job}
            utils={{
              currencyFormatter,
              approveInvoice: async (invoice: Invoice) => {
                const updatedInvoice = await approveInvoice(invoice);
                setInvoices((state) =>
                  state.map((invoice) =>
                    invoice.id === updatedInvoice.id ? updatedInvoice : invoice,
                  ),
                );
              },
              unapproveInvoice: async (invoice: Invoice) => {
                const updatedInvoice = await unapproveInvoice(invoice);
                setInvoices((state) =>
                  state.map((invoice) =>
                    invoice.id === updatedInvoice.id ? updatedInvoice : invoice,
                  ),
                );
              },
            }}
            isDayDetailsView={true}
            customHeader={
              <JobHeader job={job} toggledJob={toggledJob} onToggleJob={setToggledJob} />
            }
            key={invoice.id}
            customCategory={currentCategory}
            reload={getInvoices}
          />
        );
      });
    });
  };

  const tabs = useMemo(() => {
    return [
      {
        id: 'jobs',
        headerClassName: 'jobs-tab',
        label: t('page_headings.driver_day_shift'),
        content: (
          <JobsTabContent
            driverDay={driverDay}
            onDriverDayChange={onDriverDayChange}
            onToggledJobChange={setToggledJob}
            toggledJob={toggledJob ?? undefined}
          />
        ),
      },
      ...(canApproveDriverDays
        ? [
            {
              id: 'payables',
              headerClassName: 'jobs-tab',
              label: t('page_headings.driver_day_payables'),
              content: <DriverInvoices />,
              onSelected: () => {
                setCurrentCategory(InvoiceCategoryFilter.PAYABLES);
              },
            },
            {
              id: 'recievables',
              headerClassName: 'jobs-tab',
              label: t('page_headings.driver_day_receivables'),
              content: <DriverInvoices />,
              onSelected: () => {
                setCurrentCategory(InvoiceCategoryFilter.RECEIVABLES);
              },
            },
          ]
        : []),
    ];
  }, [
    jobsForDay,
    toggledJob,
    driverDay,
    invoices,
    canApproveDriverDays,
    driverDayId,
    onDriverDayChange,
  ]);

  useEffect(() => {
    fetchJobsForDriverDay(driverDayId);
  }, [driverDayId]);

  useEffect(() => {
    if (!toggledJob && jobsForDay && jobsForDay.length > 0) {
      setToggledJob(jobsForDay?.[0]);
    }
  }, [toggledJob, jobsForDay]);

  if (isLoading) {
    return <Skeleton height={300} />;
  }

  return (
    <Box sx={{ p: 1.5, pb: 2, borderTopWidth: '0px' }}>
      <SmallTabs
        tabs={tabs}
        sx={{ background: 'transparent' }}
        tabPanelStyle={{
          backgroundColor: theme.brandV2.colors.treadGray8,
          p: 1.5,
          pb: 2,
        }}
        tabStyle={{
          '&.MuiTab-root': {
            borderRadius: '8px 8px 0 0',
            px: 1.5,
            width: 'fit-content',
            fontSize: '12px',
          },

          background: theme.brandV2.colors.treadGray7,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          color: theme.brandV2.colors.treadGray2,
          '&.Mui-selected': {
            // text.primary is mode-aware so the selected label contrasts with
            // the treadGray8 surface in both modes; primary.contrastText is a
            // mode-fixed dark ink that sat dark-on-dark once treadGray8 darkened
            // to a near-black surface in dark mode, hiding the selected label.
            color: theme.palette.text.primary,
            background: theme.brandV2.colors.treadGray8,
            borderColor: theme.brandV2.colors.treadGray7,
          },
        }}
      />
    </Box>
  );
});

interface JobsTabContentProps {
  driverDay: DriverDay;
  onDriverDayChange: (driverDay: DriverDay) => void;
  onToggledJobChange: (job: Job) => void;
  toggledJob?: Job;
}

const JobsTabContent = observer(
  ({
    driverDay,
    onDriverDayChange,
    onToggledJobChange,
    toggledJob,
  }: JobsTabContentProps) => {
    const driverDayId = driverDay.id;
    const { driverDayStore } = useStores();
    const [forceExitEditMode, setForceExitEditMode] = useState(false);

    const jobIdsForDay = driverDayStore.jobIdsByDriverDayId[driverDayId];
    const jobsForDay = useMemo(
      () => jobIdsForDay?.map((jobId) => driverDayStore.jobByJobId[jobId]),
      [jobIdsForDay, JSON.stringify(driverDayStore.jobByJobId)],
    );

    const jobIds = useMemo(() => {
      return (jobsForDay || [])?.map((job) => job.id);
    }, [jobsForDay]);

    const handleJobUpdated = useCallback(
      (updatedJob: Job) => {
        driverDayStore.setJobByJobId(updatedJob.id, updatedJob);
      },
      [driverDayStore],
    );

    // Reset forceExitEditMode when shift approval loading changes
    useEffect(() => {
      if (driverDayStore.isShiftApprovalLoading(driverDayId)) {
        setForceExitEditMode(true);
      } else {
        setForceExitEditMode(false);
      }
    }, [driverDayStore.isShiftApprovalLoading(driverDayId), driverDayId]);

    return (
      <ApprovalsQueryContextProvider driverDayId={driverDayId}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 2 }}>
          <Box
            aria-label={`job list for ${driverDayId}`}
            component="ul"
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <ShiftDetails
              driverDay={driverDay}
              firstJobStartAt={
                jobsForDay?.[0]?.jobStartAt
                  ? dayjs.tz(jobsForDay[0].jobStartAt)
                  : dayjs.tz(driverDay.startedShiftAt)
              }
              jobsIds={jobIds}
              onDriverDayChange={onDriverDayChange}
            />

            {jobsForDay && jobsForDay.length > 0 ? (
              jobsForDay.map((job) => {
                return (
                  <JobDetails
                    key={job.id}
                    driverDay={driverDay}
                    job={job}
                    toggledJob={toggledJob ?? null}
                    onToggleJob={onToggledJobChange}
                    editable={driverDay.editable}
                    onJobUpdated={handleJobUpdated}
                    forceExitEditMode={forceExitEditMode}
                  />
                );
              })
            ) : (
              <EmptyStateMessage message={t('approvals.driver_day.no_jobs_started')} />
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <CategoryApprovalsCard
              billingApprovedAt={driverDay.billingApprovedAt}
              driverDayId={driverDayId}
              fieldApprovedAt={driverDay.fieldApprovedAt}
            />

            {toggledJob && <JobDetailsTabs toggledJob={toggledJob} />}
          </Box>
        </Box>
      </ApprovalsQueryContextProvider>
    );
  },
);
